/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */
import * as React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Document, Text } from '@contentful/rich-text-types';
import { kebabCase } from 'lodash';

import ContentfulLink from 'bundles/ent-website/contentful/core/ContentfulLink';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

type Props = {
  content: Document;
  module: BlockLevelModule;
  typographyColor?:
    | 'inherit'
    | 'error'
    | 'body'
    | 'invertBody'
    | 'supportText'
    | 'primaryHeadline'
    | 'success'
    | 'highlightBlue'
    | 'highlightPurple'
    | 'eyebrowYellow'
    | 'eyebrowAqua'
    | 'eyebrowPurple'
    | undefined;
  icon?: JSX.Element;
};

const RichText = ({ content, module, typographyColor, icon }: Props) => {
  const getRichTextOptions = (): Options => {
    let headingIdx = -1;
    let linkIdx = -1;

    return {
      /** 
          This method is ran, in order, for every node in the rich text body. It can be used to 
          specify how documentToReactComponents should render specific nodes. 
    
          When it is run, we know the location of the current node, but nothing about the node 
          itself. To counter this we use the id attribute to find the asset, entry, etc corresponding 
          to the current node, then render accordingly.
       */
      renderNode: {
        [BLOCKS.HEADING_2]: (node, children) => {
          const textBlock = node?.content?.[0] as Text;
          const kebabCaseTextContent = kebabCase(textBlock?.value);
          headingIdx += 1;
          return <h2 id={`${headingIdx}-${kebabCaseTextContent}`}>{children}</h2>;
        },
        [INLINES.HYPERLINK]: (node) => {
          linkIdx += 1;
          return (
            <ContentfulLink
              module={module}
              href={node.data.uri}
              text={(node.content[0] as Text).value}
              position={linkIdx}
              typographyColor={typographyColor}
              namespace="richTextLink"
              icon={icon}
            />
          );
        },
      },
    };
  };

  return <div className="rc-RichText">{documentToReactComponents(content, getRichTextOptions())}</div>;
};

export default RichText;
